import "./FamilyMembersList.css";
import {
  GoabContainer,
  GoabFormItem,
  GoabCheckbox,
  GoabSpacer,
  GoabButton,
  GoabText,
} from "@abgov/react-components";
import {
  domainKey,
  refCategories,
  getFullName,
} from "../../utils/globalUtility";

const FamilyMembersList = (input) => {
  const {
    familyMembers,
    setSelectedFamilyMember,
    familyMemberWasChanged,
    getResourceValue,
    getRefData,
    form,
    setFormData,
    applicationGeneration = 0,
    isGenerationTrackingEnabled,
    isEditable = true,
    handleAddFamilyMemberClick,
  } = input;

  const relationshipRefData = getRefData({
    category: refCategories.RELATIONSHIP,
    domainKey,
  });

  // Check if family member can be edited/deleted based on generation (only if feature is enabled)
  const canModifyFamilyMember = (familyMember) => {
    if (!isGenerationTrackingEnabled) {
      return true; // If the feature is disabled, allow all modifications
    }
    const memberGeneration = familyMember?.generation ?? 0;
    // Can modify if member generation matches or is higher than app generation
    return memberGeneration >= applicationGeneration;
  };

  const handleChange = (form, name, value) => {
    // Prevent updates to acceptLegalRepresentative when not editable
    if (name === "acceptLegalRepresentative" && !isEditable) {
      return;
    }
    delete form.errors[name];
    form.setFieldValue(name, value);
    if (name === "acceptLegalRepresentative" && value === true) {
      familyMemberWasChanged.current = false;
    }
    if (setFormData) {
      const formValue = { ...form.values, [name]: value };
      setFormData(formValue);
    }
  };

  return (
    <>
      {familyMembers?.length > 0 && (
        <>
          <GoabContainer maxWidth="100%" accent="thin" type="interactive">
            <h3>
              {getResourceValue({ key: "RESOURCES@familyMembers.title" })}
            </h3>
            <GoabSpacer vSpacing="l" />

            <div className="familyMembersList">
              {familyMembers.map((fm) => {
                const canModify = canModifyFamilyMember(fm);
                return (
                  <div
                    key={fm.id}
                    className="familyMemberInfo"
                  >
                    <strong className="familyMemberName">
                      {getFullName(fm.givenName, fm.middleName, fm.surname)}
                    </strong>
                    <GoabText
                      className="familyMemberRelationship"
                      mb="none"
                      mt="none"
                      color="secondary"
                    >
                      {relationshipRefData?.find(
                        (ref) => ref.code === fm.relationship,
                      )?.description ?? fm.relationship}
                    </GoabText>
                    <GoabButton
                      type="text"
                      size="compact"
                      disabled={!canModify || !isEditable}
                      onClick={() => setSelectedFamilyMember(fm)}
                    >
                      {getResourceValue({
                        key: "RESOURCES@global.button.manage",
                      })}
                    </GoabButton>
                  </div>
                );
              })}
            </div>
            <GoabSpacer vSpacing="m" />
          </GoabContainer>
          <GoabSpacer vSpacing="m" />
        </>
      )}

      <GoabButton
        type="secondary"
        leadingIcon="add"
        disabled={!isEditable}
        onClick={() => handleAddFamilyMemberClick({})}
      >
        {familyMembers?.length > 0
          ? getResourceValue({
              key: "RESOURCES@familyMembers.button.addAnotherFamilyMember",
            })
          : getResourceValue({
              key: "RESOURCES@familyMembers.button.addFamilyMember",
            })}
      </GoabButton>

      {familyMembers?.length > 0 && (
        <>
          <GoabSpacer vSpacing="2xl" />
          <GoabFormItem
            requirement="required"
            id="acceptLegalRepresentative"
            error={
              form.errors.acceptLegalRepresentative &&
              getResourceValue({
                key: form.errors.acceptLegalRepresentative,
              })
            }
          >
            <GoabCheckbox
              name="acceptLegalRepresentative"
              text={getResourceValue({
                key: "RESOURCES@familyMembers.acceptLegalRepresentative.label",
              })}
              checked={
                form.values.acceptLegalRepresentative &&
                !familyMemberWasChanged.current
              }
              error={form.errors.acceptLegalRepresentative}
              disabled={!isEditable}
              onChange={({ name, checked, value }) =>
                handleChange(form, name, checked)
              }
            ></GoabCheckbox>
          </GoabFormItem>
        </>
      )}
    </>
  );
};

export default FamilyMembersList;
